import { useModal } from 'bootstrap-vue-next'
import { markRaw } from 'vue'


const transformOptions = (options: any) => {
  const { component, props = {}, ...rest } = options

  // mark component raw to avoid Vue reactivity warnings
  const modalComponent = component ? markRaw(component) : undefined

  /**
    * NOTE: bootstrap-vue-next 0.40+ internally runs `toValue(...)`
    *   on most params (except keys equal to "component", "slots", or starting with "on").
    * That means function-valued params can be executed during option normalization.
    * If a param must remain a Function (e.g. `recordHook`, `submitHandler`), wrap it
    * as a getter: `param: () => actualFunction`, so `toValue` resolves to the function
    * itself instead of invoking it and passing its return value.
   */
  const transformedProps: Record<string, any> = {}
  for (const key in props) {
    const value = props[key]
    if (typeof value === 'function' && !key.startsWith("on") && key !== "slots") {
      // Wrap to prevent toValue from invoking it
      transformedProps[key] = () => value
    } else {
      transformedProps[key] = value
    }
  }

  return {
    component: modalComponent,
    /**
     * https://bootstrap-vue-next.github.io/bootstrap-vue-next/docs/composables/useModal.html
     * In the example in the docs, props are now passed directly in the params and not nested under a `props` key.
     * So we have to flatten the props in order to make them work.
     * This is a change from the previous implementation where props were nested under a `props` key.
     */
    ...transformedProps,
    ...rest,
  }
}

export function useBootstrapVueModal() {
  const modal = useModal()

  // For confirm modals, we want to return a boolean indicating whether the user confirmed or cancelled the modal.
  const confirmModal = async (options: any) => {
    const transformedOptions = transformOptions(options)

    const result = await modal.create(transformedOptions).show()
    return result?.ok === true
  }

  // For regular modals, we just want to show the modal without returning any result.
  const showModal = (options: any) => {
    const transformedOptions = transformOptions(options)

    const createdModal = modal.create(transformedOptions)
    createdModal.show()
    return createdModal
  }


  const hideModal = () => {
    modal.hide()
  }

  return {
    showModal,
    confirmModal,
    hideModal
  }
}